import {
    Alert,
    AlertIcon,
    Badge,
    Box,
    Checkbox,
    HStack,
    Icon,
    IconButton,
    Spinner,
    Text,
    Tooltip,
    Tr,
    VStack,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';
import { TbCar, TbCategory, TbInfoCircle, TbShieldX, TbSparkles, TbTrash } from 'react-icons/tb';
import { HiOutlineLocationMarker } from 'react-icons/hi';
import type { CatalogPositionGroup } from '../../../../hooks/useGetPCDBCatalog';
import BulkActionBar, { type BulkActionBarItem } from '../../../../components/BulkActionBar';
import {
    PARTBAY_BLUE,
    SettingsFlowForm,
    SettingsSubTable,
    SettingsTd,
    SettingsTh,
    useSubTablePagination,
} from '../../../../components/settings/primitives';
import {
    ALL_POSITIONS,
    InlinePartTypesCell,
    InlinePositionCell,
    InlineRuleInsuranceCell,
    InlineRuleMakesCell,
} from './PartAllowanceCells';
import PartAllowanceBulkModal, {
    type PartAllowanceBulkMode,
    type PartAllowanceBulkValue,
} from './PartAllowanceBulkModal';
import type { PartAllowanceDraft } from './partAllowanceDrafts';

/** Width of the step's column: wider than a form, narrower than full bleed. */
const STEP_MAX_WIDTH = '1100px';

export interface PartAllowanceDetailsStepProps {
    drafts: PartAllowanceDraft[];
    oemOptions: Array<{ _id: string; name: string }>;
    /** The list this flow is adding to prohibits specific insurers. */
    showInsurance: boolean;
    onPatch: (partTerminologyId: number, patch: Partial<PartAllowanceDraft>) => void;
    /** One value written to every ticked row in a single update. */
    onPatchMany: (
        partTerminologyIds: Set<number>,
        build: (draft: PartAllowanceDraft) => Partial<PartAllowanceDraft>,
    ) => void;
    onRemove: (partTerminologyId: number) => void;
    /** Omitted while editing one saved rule, where there is nothing to add to. */
    onSuggest?: (partTerminologyId: number) => void;
    /** The row whose suggestions are being fetched, if any. */
    suggestingId?: number | null;
}

/**
 * Step two of the add-parts flow: what each chosen part is allowed to be. The
 * columns are the ones the Part Allowances table itself shows, and the cells are
 * the same click-to-edit pickers, so a rule is set up the same way here as it is
 * changed later — but as a nested table in the step's own column rather than
 * full bleed, since the batch is normally a handful of rows.
 *
 * Setting the same position, part types, OEMs or prohibited insurance on a whole
 * batch is the common case, so ticking rows raises the bulk bar and its value
 * picker — the same pair the Part Allowances table uses.
 */
const PartAllowanceDetailsStep = ({
    drafts,
    oemOptions,
    showInsurance,
    onPatch,
    onPatchMany,
    onRemove,
    onSuggest,
    suggestingId = null,
}: PartAllowanceDetailsStepProps) => {
    /** Ticked rows, by part, so removing or inserting rows never shifts them. */
    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
    const [bulkMode, setBulkMode] = useState<PartAllowanceBulkMode | null>(null);

    const { currentPage, setCurrentPage, totalPages, pageItems } = useSubTablePagination(drafts);

    // A row dropped from the batch takes its tick with it, so the bulk bar
    // never counts parts that are no longer here.
    useEffect(() => {
        setSelectedIds((prev) => {
            if (prev.size === 0) return prev;
            const present = new Set(drafts.map((draft) => draft.partTerminologyId));
            const next = new Set(Array.from(prev).filter((id) => present.has(id)));
            return next.size === prev.size ? prev : next;
        });
    }, [drafts]);

    const selectedDrafts = useMemo(
        () => drafts.filter((draft) => selectedIds.has(draft.partTerminologyId)),
        [drafts, selectedIds],
    );

    /** Position groups every ticked part has, for the bulk position editor. */
    const sharedPositionGroups = useMemo<CatalogPositionGroup[]>(() => {
        if (selectedDrafts.length === 0) return [];
        const [first, ...rest] = selectedDrafts;
        return first.positionGroups.filter((group) =>
            rest.every((draft) =>
                draft.positionGroups.some((other) => other.groupName === group.groupName),
            ),
        );
    }, [selectedDrafts]);

    const bulkActions = useMemo<BulkActionBarItem[]>(() => {
        const actions: BulkActionBarItem[] = [
            {
                id: 'position',
                label: 'Position',
                icon: HiOutlineLocationMarker as React.ElementType,
            },
            { id: 'partTypes', label: 'Part Types', icon: TbCategory as React.ElementType },
            { id: 'makes', label: 'OEMs', icon: TbCar as React.ElementType },
        ];
        if (showInsurance) {
            actions.push({
                id: 'insurance',
                label: 'Prohibited Insurance',
                icon: TbShieldX as React.ElementType,
            });
        }
        return actions;
    }, [showInsurance]);

    const allPageSelected =
        pageItems.length > 0 &&
        pageItems.every((draft) => selectedIds.has(draft.partTerminologyId));
    const somePageSelected = pageItems.some((draft) => selectedIds.has(draft.partTerminologyId));

    const toggleRow = (partTerminologyId: number, checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            if (checked) next.add(partTerminologyId);
            else next.delete(partTerminologyId);
            return next;
        });
    };

    // Header checkbox covers the current page only, matching what is visible.
    const togglePage = (checked: boolean) => {
        setSelectedIds((prev) => {
            const next = new Set(prev);
            pageItems.forEach((draft) => {
                if (checked) next.add(draft.partTerminologyId);
                else next.delete(draft.partTerminologyId);
            });
            return next;
        });
    };

    const handleBulkApply = (value: PartAllowanceBulkValue) => {
        switch (value.mode) {
            case 'position':
                onPatchMany(selectedIds, (draft) => {
                    if (value.groupName === ALL_POSITIONS) {
                        return {
                            positionIds: draft.allPositionIds,
                            positionGroupName: ALL_POSITIONS,
                        };
                    }
                    const group = draft.positionGroups.find(
                        (entry) => entry.groupName === value.groupName,
                    );
                    if (!group) return {};
                    return {
                        positionIds: group.positionIds,
                        positionGroupName: group.groupName,
                    };
                });
                break;
            case 'partTypes':
                onPatchMany(selectedIds, () => ({ allowablePartTypes: value.partTypes }));
                break;
            case 'makes':
                onPatchMany(selectedIds, () => ({ makes: value.makes }));
                break;
            case 'insurance':
                onPatchMany(selectedIds, () => ({ prohibitedInsurance: value.insurance }));
                break;
            // The batch is destined for one list, so there is nowhere to move to.
            case 'move':
                break;
        }
        setSelectedIds(new Set());
        setBulkMode(null);
    };

    if (drafts.length === 0) {
        return (
            <SettingsFlowForm maxW={STEP_MAX_WIDTH}>
                <Alert status="info" variant="left-accent">
                    <AlertIcon />
                    <Text fontSize="sm">
                        No parts chosen yet. Go back to Choose Parts and tick the ones this
                        rule should cover.
                    </Text>
                </Alert>
            </SettingsFlowForm>
        );
    }

    const hasInsuranceSet =
        showInsurance &&
        drafts.some(
            (draft) =>
                draft.prohibitedInsurance === 'all' ||
                (Array.isArray(draft.prohibitedInsurance) &&
                    draft.prohibitedInsurance.length > 0),
        );

    return (
        <>
            <SettingsFlowForm maxW={STEP_MAX_WIDTH}>
                <VStack align="stretch" spacing={4}>
                    {hasInsuranceSet && (
                        <Alert status="warning" borderRadius="md" py={2}>
                            <AlertIcon boxSize={4} />
                            <Text fontSize="xs">
                                Setting prohibited insurance companies may affect your DRP score.
                            </Text>
                        </Alert>
                    )}

                    <SettingsSubTable
                        isInset
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                        footerLeft={
                            <Text fontSize="xs" color="gray.500">
                                {selectedIds.size > 0
                                    ? `${selectedIds.size} of ${drafts.length} selected`
                                    : `${drafts.length} part${drafts.length === 1 ? '' : 's'}`}
                            </Text>
                        }
                        head={
                            <>
                                <SettingsTh w="44px">
                                    <Checkbox
                                        size="sm"
                                        colorScheme="partbay-blue"
                                        isChecked={allPageSelected}
                                        isIndeterminate={!allPageSelected && somePageSelected}
                                        onChange={(e) => togglePage(e.target.checked)}
                                        aria-label="Select all parts on this page"
                                    />
                                </SettingsTh>
                                <SettingsTh w={showInsurance ? '23%' : '27%'}>Part</SettingsTh>
                                <SettingsTh w={showInsurance ? '15%' : '17%'}>Position</SettingsTh>
                                <SettingsTh w={showInsurance ? '21%' : '25%'}>Part Types</SettingsTh>
                                <SettingsTh w="15%">OEM(s)</SettingsTh>
                                {showInsurance && (
                                    <SettingsTh w="16%">
                                        <HStack spacing={1} align="center">
                                            <Text as="span">Prohibited Insurance</Text>
                                            <Tooltip
                                                label="If you set the prohibited insurance companies, we will not price match to this part type + part combo for that carrier"
                                                placement="top"
                                                hasArrow
                                                fontSize="xs"
                                            >
                                                <Box
                                                    as="span"
                                                    display="inline-flex"
                                                    alignItems="center"
                                                    cursor="help"
                                                >
                                                    <Icon
                                                        as={TbInfoCircle as React.ElementType}
                                                        boxSize={3.5}
                                                        color="gray.400"
                                                    />
                                                </Box>
                                            </Tooltip>
                                        </HStack>
                                    </SettingsTh>
                                )}
                                <SettingsTh w="80px" textAlign="center">
                                    Actions
                                </SettingsTh>
                            </>
                        }
                    >
                        {pageItems.map((draft) => (
                            // role="group" powers the chevron that fades in on
                            // hover for every editable cell in the row.
                            <Tr
                                key={draft.partTerminologyId}
                                role="group"
                                _hover={{ bg: 'gray.50' }}
                            >
                                <SettingsTd>
                                    <Checkbox
                                        size="sm"
                                        colorScheme="partbay-blue"
                                        isChecked={selectedIds.has(draft.partTerminologyId)}
                                        onChange={(e) =>
                                            toggleRow(draft.partTerminologyId, e.target.checked)
                                        }
                                        aria-label={`Select ${draft.partTerminologyName}`}
                                    />
                                </SettingsTd>
                                <SettingsTd overflow="hidden">
                                    <VStack align="start" spacing={0}>
                                        <HStack spacing={2} minW={0}>
                                            <Text fontWeight="medium" fontSize="sm" noOfLines={1}>
                                                {draft.partTerminologyName}
                                            </Text>
                                            {draft.isSuggested && (
                                                <Badge
                                                    colorScheme="purple"
                                                    fontSize="2xs"
                                                    variant="subtle"
                                                    flexShrink={0}
                                                >
                                                    Suggested
                                                </Badge>
                                            )}
                                        </HStack>
                                        <Text fontSize="xs" color="gray.500" noOfLines={1}>
                                            {draft.categoryName} → {draft.subcategoryName}
                                        </Text>
                                    </VStack>
                                </SettingsTd>
                                <SettingsTd overflow="hidden">
                                    <InlinePositionCell
                                        value={draft.positionGroupName}
                                        options={{
                                            groups: draft.positionGroups,
                                            allPositionIds: draft.allPositionIds,
                                        }}
                                        ariaLabel={`Edit ${draft.partTerminologyName} position`}
                                        onSelect={(next) => onPatch(draft.partTerminologyId, next)}
                                    />
                                </SettingsTd>
                                <SettingsTd overflow="hidden">
                                    <InlinePartTypesCell
                                        value={draft.allowablePartTypes}
                                        ariaLabel={`Edit ${draft.partTerminologyName} part types`}
                                        onSave={(next) =>
                                            onPatch(draft.partTerminologyId, {
                                                allowablePartTypes: next,
                                            })
                                        }
                                    />
                                </SettingsTd>
                                <SettingsTd overflow="hidden">
                                    <InlineRuleMakesCell
                                        value={draft.makes}
                                        options={oemOptions}
                                        ariaLabel={`Edit ${draft.partTerminologyName} OEMs`}
                                        onSave={(next) =>
                                            onPatch(draft.partTerminologyId, { makes: next })
                                        }
                                    />
                                </SettingsTd>
                                {showInsurance && (
                                    <SettingsTd overflow="hidden">
                                        <InlineRuleInsuranceCell
                                            value={draft.prohibitedInsurance}
                                            ariaLabel={`Edit ${draft.partTerminologyName} prohibited insurance`}
                                            onSave={(next) =>
                                                onPatch(draft.partTerminologyId, {
                                                    prohibitedInsurance: next,
                                                })
                                            }
                                        />
                                    </SettingsTd>
                                )}
                                <SettingsTd textAlign="center">
                                    <HStack spacing={1} justify="center">
                                        {onSuggest && (
                                            <Tooltip
                                                label="Suggest similar parts"
                                                openDelay={400}
                                                hasArrow
                                            >
                                                <IconButton
                                                    aria-label={`Suggest parts similar to ${draft.partTerminologyName}`}
                                                    icon={
                                                        suggestingId === draft.partTerminologyId ? (
                                                            <Spinner size="xs" />
                                                        ) : (
                                                            <Icon
                                                                as={TbSparkles as React.ElementType}
                                                                boxSize={4}
                                                            />
                                                        )
                                                    }
                                                    size="xs"
                                                    variant="ghost"
                                                    color={PARTBAY_BLUE}
                                                    _hover={{ bg: 'gray.100' }}
                                                    isDisabled={suggestingId !== null}
                                                    onClick={() =>
                                                        onSuggest(draft.partTerminologyId)
                                                    }
                                                />
                                            </Tooltip>
                                        )}
                                        <Tooltip label="Remove part" openDelay={400} hasArrow>
                                            <IconButton
                                                aria-label={`Remove ${draft.partTerminologyName}`}
                                                icon={
                                                    <Icon
                                                        as={TbTrash as React.ElementType}
                                                        boxSize={4}
                                                    />
                                                }
                                                size="xs"
                                                variant="ghost"
                                                color="red.500"
                                                _hover={{ bg: 'red.50', color: 'red.600' }}
                                                onClick={() => onRemove(draft.partTerminologyId)}
                                            />
                                        </Tooltip>
                                    </HStack>
                                </SettingsTd>
                            </Tr>
                        ))}
                    </SettingsSubTable>
                </VStack>
            </SettingsFlowForm>

            <BulkActionBar
                selectedCount={selectedIds.size}
                actions={bulkActions}
                onClearSelection={() => setSelectedIds(new Set())}
                onAction={(actionId) => setBulkMode(actionId as PartAllowanceBulkMode)}
            />

            <PartAllowanceBulkModal
                mode={bulkMode}
                selectedCount={selectedIds.size}
                sharedPositionGroups={sharedPositionGroups}
                oemOptions={oemOptions}
                // Every part in the batch is bound for the same list, so the
                // bulk bar never offers Move and this stays empty.
                moveTargets={[]}
                onApply={handleBulkApply}
                onClose={() => setBulkMode(null)}
            />
        </>
    );
};

export default PartAllowanceDetailsStep;
